import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {currentUserModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import StudyInstituteShortDto = MdbsCore.StudyInstituteShortDto;
import StudyInstituteDto = MdbsCore.StudyInstituteDto;
import IStudyInstituteCreateDto = MdbsCore.IStudyInstituteCreateDto;
import StudyInstituteCreateDto = MdbsCore.StudyInstituteCreateDto;
import IStudyInstituteUpdateDto = MdbsCore.IStudyInstituteUpdateDto;
import StudyInstituteUpdateDto = MdbsCore.StudyInstituteUpdateDto;
import IStudyInstituteDto = MdbsCore.IStudyInstituteDto;
import IStudyInstituteShortDto = MdbsCore.IStudyInstituteShortDto;
import Rights = MdbsCore.Rights;
import IStudyInstituteOverviewDto = MdbsCore.IStudyInstituteOverviewDto;

const studyInstituteClient = new MdbsCore.StudyInstituteClient();

@Module({namespaced: true, name: 'studyInstitutes', store: store})
export default class StudyInstitutesModule extends BaseModule {
    studyInstitutes = new Array<IStudyInstituteShortDto>();
    _studyInstitutesOverview = new Array<IStudyInstituteOverviewDto>();
    currentStudyInstitute: IStudyInstituteDto = new StudyInstituteDto();

    get studyInstitutesOverview() {
        return this._studyInstitutesOverview;
    }

    @Mutation
    setStudyInstitutes(studyInstitutes: Array<StudyInstituteShortDto>) {
        this.studyInstitutes.updateValues(studyInstitutes);
    }

    @Mutation
    setStudyInstitutesOverview(studyInstitutesOverview: Array<IStudyInstituteOverviewDto>) {
        this._studyInstitutesOverview.updateValues(studyInstitutesOverview);
    }

    @Mutation
    setCurrentStudyInstitute(studyInstitute: StudyInstituteDto) {
        this.currentStudyInstitute = studyInstitute;
    }

    @Mutation
    clearStudyInstitutes() {
        this.studyInstitutes.clear();
    }

    @Mutation
    clearCurrentStudyInstitute() {
        this.currentStudyInstitute = new StudyInstituteDto();
    }

    @Action({rawError: true})
    async loadAll(includeDeleted?: boolean) {
        this.startLoading();

        this.clearStudyInstitutes();
        await this.execute(async () => {
            const studyInstitutes = currentUserModule.hasRight(Rights.StudyInstituteGetAllCmd)
                ? await studyInstituteClient.getAllStudyInstitutes(includeDeleted)
                : await studyInstituteClient.getMyStudyInstitutes(includeDeleted);

            this.setStudyInstitutes(studyInstitutes);
        });

        this.stopLoading();
    }

    @Action({rawError: true})
    async loadStudyInstitutesOverview() {
        this.startLoading();

        await this.execute(async () => {
            if (currentUserModule.hasRight(Rights.StudyInstituteOverviewGetAllCmd)) {
                this.setStudyInstitutesOverview(await studyInstituteClient.getStudyInstituteOverview());
                return;
            }

            if (currentUserModule.hasRight(Rights.StudyInstituteOverviewGetMyCmd)) {
                this.setStudyInstitutesOverview(await studyInstituteClient.getMyStudyInstituteOverview());
                return;
            }

            this.setStudyInstitutesOverview(new Array<IStudyInstituteOverviewDto>());
        });

        this.stopLoading();
    }

    @Action({rawError: true})
    async getStudyInstituteById(instituteId: string) {
        this.startLoading();

        await this.execute(async () => {
            const studyInstituteDto = await studyInstituteClient.getById(instituteId);

            this.setCurrentStudyInstitute(studyInstituteDto);
        });

        this.stopLoading();
    }

    @Action({rawError: true})
    async createStudyInstitute(institute: IStudyInstituteCreateDto) {
        this.startLoading();

        await this.execute(async () => {
            await studyInstituteClient.create(new StudyInstituteCreateDto(institute));
            await this.loadAll();
        });

        this.stopLoading();
    }

    @Action({rawError: true})
    async updateStudyInstitute(institute: IStudyInstituteUpdateDto) {
        this.startLoading();

        await this.execute(async () => {
            await studyInstituteClient.update(institute.id, new StudyInstituteUpdateDto(institute));
            await this.loadAll();
        });

        this.stopLoading();
    }
}
